import {
	ArrowNarrowDown,
	ArrowNarrowUp,
	ChevronSelectorVertical,
	Copy01,
	Edit01,
	HelpCircle,
	Trash01,
} from "@untitledui/icons";
import type {
	ComponentPropsWithRef,
	HTMLAttributes,
	ReactNode,
	Ref,
	TdHTMLAttributes,
	ThHTMLAttributes,
} from "react";
import { createContext, isValidElement, useContext } from "react";
import type {
	CellProps as AriaCellProps,
	ColumnProps as AriaColumnProps,
	RowProps as AriaRowProps,
	TableHeaderProps as AriaTableHeaderProps,
	TableProps as AriaTableProps,
} from "react-aria-components";
import {
	Cell as AriaCell,
	Collection as AriaCollection,
	Column as AriaColumn,
	Group as AriaGroup,
	Row as AriaRow,
	Table as AriaTable,
	TableBody as AriaTableBody,
	TableHeader as AriaTableHeader,
	useTableOptions,
} from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";

export const TableRowActionsDropdown = () => {
	const { t } = useAppTranslation();
	return (
		<Dropdown.Root>
			<Dropdown.DotsButton />

			<Dropdown.Popover className="w-min">
				<Dropdown.Menu>
					<Dropdown.Item icon={Edit01}>
						<span className="pr-4">{t("common.actions.edit")}</span>
					</Dropdown.Item>
					<Dropdown.Item icon={Copy01}>
						<span className="pr-4">{t("common.actions.copy")}</span>
					</Dropdown.Item>
					<Dropdown.Item icon={Trash01}>
						<span className="pr-4">{t("common.actions.delete")}</span>
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown.Popover>
		</Dropdown.Root>
	);
};

const TableContext = createContext<{ size: "sm" | "md" }>({ size: "md" });

const TableCardRoot = ({
	children,
	className,
	size = "md",
	...props
}: HTMLAttributes<HTMLDivElement> & { size?: "sm" | "md" }) => {
	return (
		<TableContext.Provider value={{ size }}>
			<div
				{...props}
				className={cx(
					"overflow-hidden rounded-xl bg-primary shadow-xs ring-1 ring-secondary",
					className,
				)}
			>
				{children}
			</div>
		</TableContext.Provider>
	);
};

interface TableCardHeaderProps {
	/** The title of the table card header. */
	title: string;
	/** The badge displayed next to the title. */
	badge?: ReactNode;
	/** The description of the table card header. */
	description?: string;
	/** The content displayed after the title and badge. */
	contentTrailing?: ReactNode;
	/** The class name of the table card header. */
	className?: string;
}

const TableCardHeader = ({
	title,
	badge,
	description,
	contentTrailing,
	className,
}: TableCardHeaderProps) => {
	const { size } = useContext(TableContext);

	return (
		<div
			className={cx(
				"relative flex flex-col items-start gap-4 border-b border-secondary bg-primary px-4 md:flex-row",
				size === "sm" ? "py-4 md:px-5" : "py-5 md:px-6",
				className,
			)}
		>
			<div className="flex flex-1 flex-col gap-0.5">
				<div className="flex items-center gap-2">
					<h2
						className={cx(
							"font-semibold text-primary",
							size === "sm" ? "text-md" : "text-lg",
						)}
					>
						{title}
					</h2>
					{badge ? (
						isValidElement(badge) ? (
							badge
						) : (
							<Badge color="brand" size="sm">
								{badge}
							</Badge>
						)
					) : null}
				</div>
				{description && <p className="text-sm text-tertiary">{description}</p>}
			</div>
			{contentTrailing}
		</div>
	);
};

interface TableRootProps
	extends AriaTableProps,
		Omit<ComponentPropsWithRef<"table">, "className" | "slot" | "style"> {
	size?: "sm" | "md";
}

const TableRoot = ({ className, size = "md", ...props }: TableRootProps) => {
	const context = useContext(TableContext);

	return (
		<TableContext.Provider value={{ size: context?.size ?? size }}>
			<div className="overflow-x-auto">
				<AriaTable
					className={(state) =>
						cx(
							"w-full overflow-x-hidden",
							typeof className === "function" ? className(state) : className,
						)
					}
					{...props}
				/>
			</div>
		</TableContext.Provider>
	);
};
TableRoot.displayName = "Table";

interface TableHeaderProps<T extends object>
	extends AriaTableHeaderProps<T>,
		Omit<
			ComponentPropsWithRef<"thead">,
			"children" | "className" | "slot" | "style"
		> {
	bordered?: boolean;
}

const TableHeader = <T extends object>({
	columns,
	children,
	bordered = true,
	className,
	...props
}: TableHeaderProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior, selectionMode } = useTableOptions();

	return (
		<AriaTableHeader
			{...props}
			className={(state) =>
				cx(
					"relative h-10 bg-tertiary",
					bordered &&
						"[&>tr>th]:after:pointer-events-none [&>tr>th]:after:absolute [&>tr>th]:after:inset-x-0 [&>tr>th]:after:bottom-0 [&>tr>th]:after:h-px [&>tr>th]:after:bg-border-secondary [&>tr>th]:focus-visible:after:bg-transparent",
					selectionBehavior !== "toggle" &&
						"[&>tr>th:first-child]:pl-5 [&>tr>th:last-child]:pr-8",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{selectionBehavior === "toggle" && (
				// Figma Table/SelectionCellHeader — h-40, pl-32 pr-4 py-12, w-52
				<AriaColumn className="relative h-10 w-[52px] bg-tertiary py-3 pr-1 pl-8">
					{selectionMode === "multiple" && (
						<div className="flex items-center">
							<Checkbox slot="selection" size={size} />
						</div>
					)}
				</AriaColumn>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaTableHeader>
	);
};

TableHeader.displayName = "TableHeader";

interface TableHeadProps
	extends AriaColumnProps,
		Omit<
			ThHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "style" | "id"
		> {
	label?: string;
	/** Optional overline above the column label (Figma hasOverline). */
	overline?: string;
	tooltip?: string;
}

const TableHead = ({
	className,
	tooltip,
	label,
	overline,
	children,
	...props
}: TableHeadProps) => {
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaColumn
			{...props}
			className={(state) =>
				cx(
					"group/header relative h-10 bg-tertiary p-0 px-6 py-1 outline-hidden transition-colors hover:bg-quaternary focus-visible:z-1 focus-visible:ring-2 focus-visible:ring-focus-ring focus-visible:ring-inset",
					selectionBehavior === "toggle" && "nth-2:pl-3",
					state.allowsSorting && "cursor-pointer",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{(state) => (
				<AriaGroup className="flex items-center gap-1">
					<div className="flex flex-col items-start gap-0.5 overflow-hidden">
						{overline ? (
							<span className="text-xs font-semibold leading-3 whitespace-nowrap text-quaternary">
								{overline}
							</span>
						) : null}
						<div className="flex items-center gap-1 text-sm font-bold leading-[14px] whitespace-nowrap text-primary">
							{label ? <span>{label}</span> : null}
							{typeof children === "function" ? children(state) : children}
						</div>
					</div>

					{tooltip && (
						<Tooltip title={tooltip} placement="top">
							<TooltipTrigger className="cursor-pointer text-fg-quaternary transition duration-100 ease-linear hover:text-fg-quaternary_hover focus:text-fg-quaternary_hover">
								<HelpCircle className="size-4" />
							</TooltipTrigger>
						</Tooltip>
					)}

					{state.allowsSorting &&
						(state.sortDirection === "ascending" ? (
							<ArrowNarrowUp className="size-4.5 shrink-0 text-fg-quaternary" />
						) : state.sortDirection === "descending" ? (
							<ArrowNarrowDown className="size-4.5 shrink-0 text-fg-quaternary" />
						) : (
							<ChevronSelectorVertical className="size-5 shrink-0 text-fg-quaternary" />
						))}
				</AriaGroup>
			)}
		</AriaColumn>
	);
};
TableHead.displayName = "TableHead";

interface TableRowProps<T extends object>
	extends AriaRowProps<T>,
		Omit<
			ComponentPropsWithRef<"tr">,
			"children" | "className" | "onClick" | "slot" | "style" | "id"
		> {
	highlightSelectedRow?: boolean;
}

const TableRow = <T extends object>({
	columns,
	children,
	className,
	highlightSelectedRow = true,
	...props
}: TableRowProps<T>) => {
	const { size } = useContext(TableContext);
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaRow
			{...props}
			className={(state) =>
				cx(
					"relative h-[50px] bg-primary outline-focus-ring transition-colors after:pointer-events-none hover:bg-[rgba(181,71,8,0.05)] focus-visible:shadow-[0_0_0_2px_var(--color-bg-primary),0_0_0_4px_var(--color-focus-ring)] focus-visible:outline-none",
					highlightSelectedRow && "selected:bg-[rgba(181,71,8,0.1)]",
					selectionBehavior !== "toggle" &&
						"[&>td:first-child]:pl-5 [&>td:last-child]:pr-8",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{selectionBehavior === "toggle" && (
				<AriaCell className="relative h-[50px] py-3 pr-1 pl-8">
					<div className="flex items-center">
						<Checkbox slot="selection" size={size} />
					</div>
				</AriaCell>
			)}
			<AriaCollection items={columns}>{children}</AriaCollection>
		</AriaRow>
	);
};

TableRow.displayName = "TableRow";

interface TableCellProps
	extends AriaCellProps,
		Omit<
			TdHTMLAttributes<HTMLTableCellElement>,
			"children" | "className" | "style" | "id"
		> {
	ref?: Ref<HTMLTableCellElement>;
}

const TableCell = ({ className, children, ...props }: TableCellProps) => {
	const { selectionBehavior } = useTableOptions();

	return (
		<AriaCell
			{...props}
			className={(state) =>
				cx(
					"relative h-[50px] px-5 py-3 text-sm font-semibold text-secondary outline-focus-ring focus-visible:z-1 focus-visible:outline-2 focus-visible:-outline-offset-2",
					selectionBehavior === "toggle" && "nth-2:pl-3",
					typeof className === "function" ? className(state) : className,
				)
			}
		>
			{children}
		</AriaCell>
	);
};
TableCell.displayName = "TableCell";

const TableCard = {
	Root: TableCardRoot,
	Header: TableCardHeader,
};

const Table = TableRoot as typeof TableRoot & {
	Body: typeof AriaTableBody;
	Cell: typeof TableCell;
	Head: typeof TableHead;
	Header: typeof TableHeader;
	Row: typeof TableRow;
};
Table.Body = AriaTableBody;
Table.Cell = TableCell;
Table.Head = TableHead;
Table.Header = TableHeader;
Table.Row = TableRow;

export { Table, TableCard };
